import { useEffect, useMemo, useState } from "react";
import Reveal from "@/components/Reveal";
import {
  Sun,
  Moon,
  ArrowUpRight,
  Quote,
  Sparkles,
  Eye,
  Skull,
} from "lucide-react";
import { useSiteContent, useLinks } from "@/admin/SiteContent";

const FALLBACK = {
  hero: {
    kicker: "L'Universo · Saga",
    title_lead: "Il Destino",
    title_accent: "di Los Santos",
    intro: "Una città. Due verità. Quattro Atti per riscrivere ogni regola.",
  },
  architecture: {
    kicker: "Architettura del Potere",
    title: "Due Realtà, una Sola Città",
    intro: "",
    light: { tag: "", title: "Il Sogno di Vetro", subtitle: "", body: "", tenets: [] },
    shadow: { tag: "", title: "L'Abisso delle Ombre", subtitle: "", body: "", tenets: [] },
  },
  rumors: [],
  outro: { kicker: "", title: "", body: "", cta_label: "Entra ora" },
};

/* ---------- Floating particles for the architecture section ---------- */
const SOGNO_PARTICLES = Array.from({ length: 18 });
const OMBRA_PARTICLES = Array.from({ length: 22 });

export const Universe = () => {
  const { content } = useSiteContent();
  const lk = useLinks();
  const u = useMemo(() => {
    const raw = (content && content.universe) || {};
    return {
      hero: { ...FALLBACK.hero, ...(raw.hero || {}) },
      architecture: {
        ...FALLBACK.architecture,
        ...(raw.architecture || {}),
        light: { ...FALLBACK.architecture.light, ...((raw.architecture || {}).light || {}) },
        shadow: { ...FALLBACK.architecture.shadow, ...((raw.architecture || {}).shadow || {}) },
      },
      rumors: Array.isArray(raw.rumors) ? raw.rumors : FALLBACK.rumors,
      outro: { ...FALLBACK.outro, ...(raw.outro || {}) },
    };
  }, [content]);

  /* Rumor carousel — cinematic loop */
  const [rumorIdx, setRumorIdx] = useState(0);
  useEffect(() => {
    if (u.rumors.length === 0) return;
    const id = setInterval(() => {
      setRumorIdx((x) => (x + 1) % u.rumors.length);
    }, 4200);
    return () => clearInterval(id);
  }, [u.rumors.length]);

  return (
    <section
      id="universo"
      data-testid="universe-root"
      className="universe-root relative overflow-hidden"
    >
      {/* Aurora background */}
      <div aria-hidden="true" className="universe-aurora" />

      {/* HERO */}
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-32 pb-24 sm:pt-40 sm:pb-32">
        <Reveal>
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="h-px w-12 bg-[#D4AF37]/60" />
            <span className="font-mono-pl text-[10px] uppercase tracking-[0.42em] text-[#D4AF37]">
              {u.hero.kicker}
            </span>
            <span className="h-px w-12 bg-[#D4AF37]/60" />
          </div>
        </Reveal>
        <Reveal delay={1}>
          <h2 className="text-center font-display text-[clamp(2.6rem,9.5vw,8.5rem)] uppercase leading-[0.85] tracking-wide">
            <span className="text-[#F5F5F5] glow-gold-strong">{u.hero.title_lead}</span>
            <br />
            <span className="text-gold-bright glow-gold-strong italic-display">
              {u.hero.title_accent}
            </span>
          </h2>
        </Reveal>
        <Reveal delay={3}>
          <p className="mt-10 text-center text-[#B6B6BE] text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto">
            {u.hero.intro}
          </p>
        </Reveal>

        {/* Scroll hint */}
        <Reveal delay={4}>
          <div className="mt-16 flex justify-center">
            <div className="universe-scroll-hint">
              <span className="font-mono-pl text-[9px] uppercase tracking-[0.32em] text-[#D4AF37]">
                Scorri per scoprire
              </span>
              <span className="universe-scroll-line" />
            </div>
          </div>
        </Reveal>
      </div>

      {/* ARCHITECTURE — Split-screen Sogno vs Ombre */}
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="text-center mb-14">
            <p className="font-mono-pl text-[10px] uppercase tracking-[0.42em] text-[#D4AF37]">
              {u.architecture.kicker}
            </p>
            <h3 className="mt-4 font-display text-3xl sm:text-5xl uppercase text-[#F5F5F5] tracking-wide">
              {u.architecture.title}
            </h3>
            {u.architecture.intro && (
              <p className="mt-4 text-[#B6B6BE] max-w-2xl mx-auto">{u.architecture.intro}</p>
            )}
          </div>
        </Reveal>

        <div className="universe-arch grid sm:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-white/8">
          {/* LIGHT — Sogno di Vetro */}
          <ArchPanel
            variant="light"
            icon={<Sun size={14} strokeWidth={2} />}
            data={u.architecture.light}
            particles={SOGNO_PARTICLES}
          />
          {/* SHADOW — Abisso delle Ombre */}
          <ArchPanel
            variant="shadow"
            icon={<Moon size={14} strokeWidth={2} />}
            data={u.architecture.shadow}
            particles={OMBRA_PARTICLES}
          />
        </div>
      </div>

      {/* RUMORS — Mysterious carousel */}
      {u.rumors.length > 0 && (
        <div className="relative mx-auto max-w-5xl px-5 sm:px-8 py-24">
          <Reveal>
            <div className="text-center mb-12">
              <p className="font-mono-pl text-[10px] uppercase tracking-[0.42em] text-[#E26B6B]">
                Voci della Strada
              </p>
              <h3 className="mt-4 font-display text-2xl sm:text-4xl uppercase text-[#F5F5F5] tracking-wide">
                Quello che si dice — a voce bassa
              </h3>
            </div>
          </Reveal>

          <div className="universe-rumor-stage">
            <Quote
              size={36}
              className="universe-rumor-quote-mark"
              strokeWidth={1.2}
              aria-hidden="true"
            />
            <div className="universe-rumor-textarea">
              {u.rumors.map((r, i) => (
                <p
                  key={i}
                  data-testid={`universe-rumor-${i}`}
                  className={`universe-rumor ${i === rumorIdx ? "universe-rumor--active" : ""}`}
                >
                  {r}
                </p>
              ))}
            </div>
            <div className="universe-rumor-dots" aria-hidden="true">
              {u.rumors.map((_, i) => (
                <span
                  key={i}
                  className={`universe-rumor-dot ${i === rumorIdx ? "universe-rumor-dot--active" : ""}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* OUTRO — CTA */}
      <div className="relative mx-auto max-w-4xl px-5 sm:px-8 py-32 text-center">
        <Reveal>
          <div className="inline-flex items-center gap-3 mb-6">
            <Skull size={14} className="text-[#D4AF37]" />
            <span className="font-mono-pl text-[10px] uppercase tracking-[0.42em] text-[#D4AF37]">
              {u.outro.kicker}
            </span>
            <Sparkles size={14} className="text-[#D4AF37]" />
          </div>
        </Reveal>
        <Reveal delay={1}>
          <h3 className="font-display text-3xl sm:text-5xl lg:text-6xl uppercase text-[#F5F5F5] tracking-wide leading-tight glow-gold-strong">
            {u.outro.title}
          </h3>
        </Reveal>
        <Reveal delay={2}>
          <p className="mt-8 text-[#B6B6BE] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            {u.outro.body}
          </p>
        </Reveal>
        <Reveal delay={3}>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <a
              href={lk.whitelist}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="universe-cta-whitelist"
              className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-bold uppercase tracking-wider"
            >
              {u.outro.cta_label}
              <ArrowUpRight size={16} strokeWidth={2.6} />
            </a>
            <a
              href={lk.discord}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost-neon inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider"
            >
              <Eye size={14} /> Osserva il prossimo Atto
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------- Architecture panel (light or shadow) ---------- */
const ArchPanel = ({ variant, icon, data, particles }) => {
  const light = variant === "light";
  return (
    <div
      className={`universe-arch__panel universe-arch__panel--${variant}`}
      data-testid={`universe-arch-${variant}`}
    >
      {/* Particles layer */}
      <div className="universe-arch__particles" aria-hidden="true">
        {particles.map((_, i) => (
          <span
            key={i}
            className="universe-arch__particle"
            style={{
              left: `${(i * 53) % 100}%`,
              top: `${(i * 37) % 100}%`,
              animationDelay: `${(i * 0.31) % 5}s`,
              animationDuration: `${5 + ((i * 1.7) % 7)}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 p-8 sm:p-12 h-full flex flex-col">
        <div className="inline-flex items-center gap-2.5 self-start">
          <span
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              light
                ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/35"
                : "bg-[#E26B6B]/10 text-[#E26B6B] border border-[#E26B6B]/35"
            }`}
          >
            {icon}
          </span>
          <span
            className={`font-mono-pl text-[9px] uppercase tracking-[0.32em] ${
              light ? "text-[#D4AF37]" : "text-[#E26B6B]"
            }`}
          >
            {data.tag}
          </span>
        </div>

        <h4
          className={`mt-8 font-display text-3xl sm:text-4xl uppercase tracking-wide leading-tight ${
            light ? "text-[#F5F5F5] glow-gold" : "text-[#F5F5F5]"
          }`}
        >
          {data.title}
        </h4>
        {data.subtitle && (
          <p
            className={`mt-2 text-sm italic ${
              light ? "text-[#D4AF37]/80" : "text-[#E26B6B]/80"
            }`}
          >
            {data.subtitle}
          </p>
        )}

        <p className="mt-8 text-[#B6B6BE] leading-relaxed text-sm sm:text-base flex-1">
          {data.body}
        </p>

        {data.tenets?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-white/[0.06]">
            <p className="font-mono-pl text-[9px] uppercase tracking-[0.32em] text-[#B6B6BE]/60 mb-3">
              {light ? "I Quattro Pilastri" : "I Quattro Vincoli"}
            </p>
            <div className="flex flex-wrap gap-2">
              {data.tenets.map((t, i) => (
                <span
                  key={i}
                  className={`universe-tenet universe-tenet--${variant}`}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default Universe;
